import { ArrowUpRight, Briefcase, Globe2, GraduationCap, Plane, Stethoscope } from "lucide-react";
import { Link } from "react-router-dom";
import { admissionCategories } from "../../../data/homepage";
import Reveal from "../../ui/Reveal";
import Section from "../../ui/Section";

const categoryIcons = [Plane, GraduationCap, Briefcase, Stethoscope, Globe2];

function AdmissionsOverview() {
  return (
    <Section id="admissions-overview" tone="white" className="border-t border-brand-line/45">
      <div className="flex flex-col justify-between gap-8 lg:flex-row lg:items-end">
        <div className="max-w-2xl">
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-brand-gold-strong">
            Strategic Pathways
          </p>
          <h2 className="mt-4 text-3xl font-semibold tracking-tight text-brand-navy sm:text-5xl">
            Choose your pathway and move forward with clarity
          </h2>
          <p className="mt-4 text-base text-brand-charcoal/72">
            Each pathway is structured for different student goals — whether studying in India or pursuing an international degree abroad.
          </p>
        </div>
        <div className="hidden h-px w-24 bg-brand-gold-strong/60 lg:block" />
      </div>

      <div className="mt-14 grid gap-4 md:grid-cols-2 xl:grid-cols-5">
        {admissionCategories.map((category, index) => {
          const Icon = categoryIcons[index] || GraduationCap;
          const isExternal = category.external && category.href;

          return (
            <Reveal key={category.title} delay={index * 0.06}>
              <article
                className={`group h-full border border-brand-line/50 bg-white p-8 transition-all duration-500 hover:text-white ${
                  isExternal
                    ? "hover:bg-brand-gold-strong"
                    : "hover:bg-brand-navy"
                }`}
              >
                <span className="text-3xl font-semibold tracking-tight text-brand-gold-strong">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <h3 className="mt-6 text-xl font-semibold leading-snug text-brand-navy transition-colors duration-500 group-hover:text-white">
                  {category.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-brand-charcoal/74 transition-colors duration-500 group-hover:text-white/76">
                  {category.summary || category.description}
                </p>

                <div className="mt-8 flex items-center justify-between">
                  <Icon className="h-5 w-5 text-brand-gold-strong transition-colors duration-500 group-hover:text-white/80" />
                  {isExternal ? (
                    <a
                      href={category.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.16em] text-brand-navy transition-colors duration-500 group-hover:text-white"
                    >
                      Explore
                      <ArrowUpRight className="h-3.5 w-3.5" />
                    </a>
                  ) : (
                    <Link
                      to={category.to}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.16em] text-brand-navy transition-colors duration-500 group-hover:text-brand-gold-soft"
                    >
                      Explore
                      <ArrowUpRight className="h-3.5 w-3.5" />
                    </Link>
                  )}
                </div>
              </article>
            </Reveal>
          );
        })}
      </div>

      {/* NextEra Abroad callout */}
      <div className="mt-8 flex items-center justify-center gap-3 rounded-2xl border border-brand-gold/30 bg-brand-gold-soft/20 px-6 py-4">
        <Plane className="h-4 w-4 text-brand-gold-strong" />
        <p className="text-sm font-semibold text-brand-navy">
          Undergraduate Abroad programs are powered by our partner{" "}
          <a
            href="https://nexteraabroad.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-brand-gold-strong underline underline-offset-2 hover:text-brand-gold"
          >
            NextEra Abroad
          </a>{" "}
          — covering 9 study destinations worldwide.
        </p>
      </div>
    </Section>
  );
}

export default AdmissionsOverview;
